import React, {useEffect} from "react";
import {Redirect, Route} from "react-router-dom";
import {hasAnyRole} from "../../auth/rbac";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {initializeSession} from "../../store/slices/authSlice";

interface PrivateRouteProps {
    component: React.ComponentType<any>;
    path?: string;
    exact?: boolean;
    requiredRoles?: readonly string[];
}

type AccessState = 'loading' | 'allowed' | 'unauthenticated' | 'forbidden';

const PrivateRoute: React.FC<PrivateRouteProps> = ({component: Component, requiredRoles, ...rest}: any) => {
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector((state) => state.auth.isLoading);
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
    const userRoles = useAppSelector((state) => state.user.profile?.roles);

    useEffect(() => {
        if (!isLoading) {
            return;
        }

        dispatch(initializeSession());
    }, [dispatch, isLoading]);

    const accessState: AccessState = (() => {
        if (isLoading) {
            return 'loading';
        }

        if (!isAuthenticated) {
            return 'unauthenticated';
        }

        if (requiredRoles?.length && !hasAnyRole(userRoles, requiredRoles)) {
            return 'forbidden';
        }

        return 'allowed';
    })();

    if (accessState === 'loading') return null;

    const renderComponent = (props: any) => {
        if (accessState === 'allowed') {
            return <Component {...props} />;
        }

        if (accessState === 'forbidden') {
            return <Redirect to="/"/>;
        }

        if (accessState === 'unauthenticated') {
            return <Redirect to="/login"/>;
        }

        return null;
    };

    return (
        <Route {...rest} render={renderComponent}/>
    );
};

export default PrivateRoute;
